import { apiClient, handleResponse, handleError } from '../helpers';
import { api } from '../constants';
import {
    createRequestCacheKey,
    fetchWithCache,
    invalidateRequestCache,
} from '../helpers/requestCache';
import { collectMediaUrls, preloadMediaUrls } from '../helpers/mediaPreloader';

const MYPROFILE_TTL_MS = 5 * 60 * 1000;
const MYPOINTS_TTL_MS = 20 * 1000;
const SCOREBOARD_TTL_MS = 30 * 1000;

const myProfileCacheKey = (studentId) => createRequestCacheKey('profile', 'myprofile', studentId);
const myPointsCacheKey = (studentId, isGameSubmit) => createRequestCacheKey('profile', 'mypoints', studentId, isGameSubmit ? 'game-submit' : 'default');
const scoreBoardCacheKey = (classId) => createRequestCacheKey('profile', 'scoreboard', classId);

export const profileService = {
    fetchMyprofile,
    updateProfileAvatar,
    updateMyprofile,
    fetchMypoints,
    fetchScoreBoard,
    purchaseProfileAvatar
};

function normalizeFavoriteString(favoriteString) {
    if (Array.isArray(favoriteString)) {
        return favoriteString;
    }

    if (typeof favoriteString === 'string') {
        const trimmedFavoriteString = favoriteString.trim();

        if (!trimmedFavoriteString) {
            return [];
        }

        try {
            const parsedFavoriteString = JSON.parse(trimmedFavoriteString);
            return Array.isArray(parsedFavoriteString) ? parsedFavoriteString : [];
        } catch {
            return [];
        }
    }

    if (favoriteString && typeof favoriteString === 'object') {
        return [favoriteString];
    }

    return [];
}

function normalizeProfileResponse(response) {
    const rawProfile =
        response && typeof response === 'object' && 'data' in response && response.data != null
            ? response.data
            : response;

    const profileList = Array.isArray(rawProfile)
        ? rawProfile
        : rawProfile && typeof rawProfile === 'object'
            ? [rawProfile]
            : [];

    return profileList
        .filter(profile => profile && typeof profile === 'object')
        .map(profile => ({
            ...profile,
            FavoriteString: normalizeFavoriteString(profile.FavoriteString),
        }));
}

function fetchMyprofile(studentId) {
    return fetchWithCache({
        key: myProfileCacheKey(studentId),
        ttlMs: MYPROFILE_TTL_MS,
        loader: () => apiClient.get(`${api.myprofile.fetchmyprofile}/${studentId}`)
            .then(handleResponse)
            .then(response => {
                const profile = normalizeProfileResponse(response);
                preloadMediaUrls(collectMediaUrls(profile, ['AvatarMedia']));
                return profile;
            }),
    })
        .catch(error => handleError(error.response));
}

function updateProfileAvatar(avatarDetails) {
    return apiClient.put(api.myprofile.updateavatar, avatarDetails)
        .then(handleResponse)
        .then(data => {
            invalidateRequestCache([
                createRequestCacheKey('profile', 'myprofile'),
                createRequestCacheKey('user', 'profile-avatars'),
            ]);
            return data;
        })
        .catch(error => handleError(error.response));
}

function updateMyprofile(profileData) {
    return apiClient.put(api.myprofile.updatemyprofile, profileData)
        .then(handleResponse)
        .then(data => {
            invalidateRequestCache(createRequestCacheKey('profile', 'myprofile'));
            return data;
        })
        .catch(error => handleError(error.response));
}

function fetchMypoints(studentId, isGameSubmit) {
    let url = `${api.myprofile.fetchmypoints}?StudentProfileID=${studentId}`;
    if (isGameSubmit) {
        url += '&isGameSubmit=true';
    }

    return fetchWithCache({
        key: myPointsCacheKey(studentId, isGameSubmit),
        ttlMs: MYPOINTS_TTL_MS,
        loader: () => apiClient.get(url)
            .then(handleResponse)
            .then(data => data),
    })
        .catch(error => handleError(error.response));
}

function fetchScoreBoard(classId) {
    return fetchWithCache({
        key: scoreBoardCacheKey(classId),
        ttlMs: SCOREBOARD_TTL_MS,
        loader: () => apiClient.get(`${api.myprofile.fetchscoreboard}/${classId}`)
            .then(handleResponse)
            .then(data => data),
    })
        .catch(error => handleError(error.response));
}

function purchaseProfileAvatar(payload) {
    return apiClient.post(api.myprofile.purchaseAvatar, payload)
        .then(handleResponse)
        .then(data => {
            invalidateRequestCache([
                createRequestCacheKey('profile', 'myprofile'),
                createRequestCacheKey('profile', 'mypoints'),
                createRequestCacheKey('user', 'profile-avatars'),
            ]);
            return data;
        })
        .catch(error => handleError(error.response));
}
